import React, { useEffect, useState, useCallback } from 'react';
import { MessageSquare, Send, Loader2, Check, X, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { api, useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

function CommentRow({ c, isOrganizer, onModerate, accent }) {
  const pending = c.status === 'pending';
  return (
    <div className={`py-3 border-b border-black/5 last:border-0 ${pending ? 'opacity-90' : ''}`} data-testid={`comment-item-${c.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-white font-semibold text-[12px]" style={{ background: pending ? '#94a3b8' : accent }}>
          {(c.authorName || 'U').substring(0, 1).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-[13.5px] text-neutral-900">{c.isMine ? 'Vous' : c.authorName}</span>
            {pending && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="comment-pending-badge">
                <Clock className="w-3 h-3" /> En attente de modération
              </span>
            )}
            <span className="text-[11px] text-neutral-400">{c.createdAt ? new Date(c.createdAt).toLocaleDateString('fr-FR') : ''}</span>
          </div>
          <p className="mt-1 text-[13.5px] text-neutral-700 leading-snug">{c.text}</p>
          {isOrganizer && pending && (
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                data-testid={`comment-approve-${c.id}`}
                onClick={() => onModerate(c.id, 'approve')}
                className="inline-flex items-center gap-1 text-[12px] font-medium px-3 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
              >
                <Check className="w-3 h-3" /> Approuver
              </button>
              <button
                type="button"
                data-testid={`comment-reject-${c.id}`}
                onClick={() => onModerate(c.id, 'reject')}
                className="inline-flex items-center gap-1 text-[12px] font-medium px-3 py-1 rounded-full bg-white border border-black/10 text-neutral-600 hover:border-red-300 hover:text-red-600 transition-colors"
              >
                <X className="w-3 h-3" /> Rejeter
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const CommentsSection = ({ cagnotteId, accent }) => {
  const { user, requireAuth } = useAuth();
  const { toast } = useToast();
  const [comments, setComments] = useState([]);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cagnottes/${cagnotteId}/comments`);
      setComments(data.items || []);
      setIsOrganizer(Boolean(data.isOrganizer));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('[CommentsSection] chargement impossible', e?.response?.status);
    }
  }, [cagnotteId]);

  useEffect(() => { load(); }, [load, user]);

  const submit = async () => {
    const t = text.trim();
    if (!t || sending) return;
    if (!requireAuth('comment')) return;
    setSending(true);
    try {
      const { data } = await api.post(`/cagnottes/${cagnotteId}/comments`, { text: t });
      setText('');
      setComments((prev) => [{ ...data.comment, isMine: true }, ...prev]);
      toast({
        title: data.comment.status === 'approved' ? 'Commentaire publié' : 'Commentaire envoyé',
        description: data.comment.status === 'approved' ? 'Votre commentaire est visible.' : "Il sera visible après validation par l'organisateur.",
      });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || "Impossible d'envoyer le commentaire." });
    } finally { setSending(false); }
  };

  const moderate = async (id, action) => {
    try {
      await api.post(`/comments/${id}/moderate`, { action });
      setComments((prev) => action === 'reject'
        ? prev.filter((c) => c.id !== id)
        : prev.map((c) => (c.id === id ? { ...c, status: 'approved' } : c)));
    } catch (e) {
      toast({ title: 'Erreur', description: 'Action de modération impossible.' });
    }
  };

  const pendingCount = comments.filter((c) => c.status === 'pending').length;

  return (
    <div className="rounded-3xl bg-white border border-black/5 p-6 sm:p-7" data-testid="comments-section">
      <div className="flex items-center gap-2">
        <MessageSquare className="w-4 h-4" style={{ color: accent }} />
        <h2 className="text-[18px] font-semibold text-[#1a1d1a]">Commentaires ({comments.length})</h2>
        {isOrganizer && pendingCount > 0 && (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{pendingCount} à modérer</span>
        )}
      </div>
      <p className="mt-1.5 text-[11.5px] text-neutral-500">Les commentaires sont vérifiés par l'organisateur avant publication.</p>

      <div className="mt-4 flex items-start gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 300))}
          placeholder={user ? 'Laissez un mot d\u2019encouragement…' : 'Connectez-vous pour commenter…'}
          data-testid="comment-input"
          rows={2}
          className="flex-1 rounded-2xl bg-[#f5f4ef] px-4 py-3 text-[13.5px] outline-none focus:ring-2 resize-none"
          style={{ '--tw-ring-color': `${accent}66` }}
          onClick={() => { if (!user) requireAuth('comment'); }}
        />
        <Button
          onClick={submit}
          disabled={!text.trim() || sending}
          data-testid="comment-submit-btn"
          className="btn-anim h-11 rounded-full px-4 text-white shrink-0"
          style={{ background: accent }}
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>

      {comments.length === 0 ? (
        <div className="mt-4 text-center py-8 text-[13px] text-neutral-500">Aucun commentaire pour l'instant. Lancez la conversation !</div>
      ) : (
        <div className="mt-3">
          {comments.map((c) => <CommentRow key={c.id} c={c} isOrganizer={isOrganizer} onModerate={moderate} accent={accent} />)}
        </div>
      )}
    </div>
  );
};
